import React from "react";
import { LegalLayout } from "@/components/site/LegalLayout";
import { COMPANY } from "@/lib/constants";

const CANONICAL = "https://alldaypowerwashing.com/terms";

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="Website terms"
      description="Terms of Service for use of the All Day Power Washing website and services."
      canonical={CANONICAL}
      testId="terms-page"
    >
      <p className="text-slate-400 text-sm">Last updated: December 2025</p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        By accessing or using the {COMPANY.name} website (the "Site"), requesting a
        quote, or scheduling services through us, you ("you" or "Client") agree to
        be bound by these Terms of Service ("Terms"). If you do not agree, please
        do not use this Site or request services.
      </p>

      <h2>2. About Us</h2>
      <p>
        {COMPANY.name} is a Colorado-based, veteran-owned and veteran-operated
        residential and commercial pressure-washing company located at{" "}
        {COMPANY.address.full}. You can reach us at {COMPANY.phone} or{" "}
        {COMPANY.email}.
      </p>

      <h2>3. Services</h2>
      <p>
        We provide power washing, concrete cleaning, gutter cleaning, house
        soft-washing, commercial fleet and heavy equipment cleaning, NFPA-96
        kitchen hood cleaning, and related exterior cleaning services to clients
        in Colorado Springs, the Denver metro area, and surrounding states by
        request. Service availability, scheduling, and pricing are subject to our
        review of each job and may be adjusted based on on-site conditions.
      </p>

      <h2>4. Quotes and Estimates</h2>
      <p>
        Instant price estimates shown through the online quote calculator are
        non-binding starting ranges only. The final quoted price is confirmed
        after a photo review or site visit. A minimum charge of $150 applies to
        every job. Residential rates are calculated at $0.35 per square foot and
        commercial rates at $0.06 per square foot, subject to site conditions.
        Accepting a written estimate constitutes a binding agreement under our{" "}
        <a href="/contract" className="text-[#1E88E5] hover:text-[#42A5F5]">
          Customer Contract
        </a>
        .
      </p>

      <h2>5. Customer Contract</h2>
      <p>
        Our full Customer Contract governs the work itself — payment, deposits,
        damages, roof and house-wash acknowledgments, exclusions, and
        liability. Please review it at{" "}
        <a href="/contract" className="text-[#1E88E5] hover:text-[#42A5F5]">
          /contract
        </a>
        . By accepting a quote or scheduling service, you agree to that Customer
        Contract in addition to these Terms.
      </p>

      <h2>6. Communications &amp; SMS Consent</h2>
      <p>
        By submitting your phone number through our quote form, you consent to
        receive transactional SMS text messages from {COMPANY.name} related to
        your quote, scheduling, and service updates. Message and data rates may
        apply. Message frequency varies. Reply <strong>STOP</strong> to opt out at
        any time; reply <strong>HELP</strong> for help. We will not sell or
        rent your phone number to any third party.
      </p>

      <h2>7. Use of This Site</h2>
      <p>
        You agree to use this Site only for lawful purposes and in a manner that
        does not infringe the rights of, or restrict or inhibit the use and
        enjoyment of, this Site by any third party. Prohibited behavior includes
        scraping at scale, attempting to breach security, or submitting false
        quote requests.
      </p>

      <h2>8. Intellectual Property</h2>
      <p>
        All content on this Site — including the {COMPANY.name} name, logo
        (elephant mark), text, photographs of our crews and jobsites, and
        graphics — is owned by {COMPANY.name} or used with permission and is
        protected by U.S. copyright and trademark law. You may not reproduce,
        redistribute, or create derivative works without our written consent.
      </p>

      <h2>9. Testimonials &amp; Reviews</h2>
      <p>
        Testimonials and reviews shown on this Site reflect the real
        experiences of customers who have worked with us. Review content
        sourced from third-party platforms (e.g., Google Business Profile) is
        displayed under fair-use summaries and may be updated periodically.
      </p>

      <h2>10. Disclaimer of Warranties</h2>
      <p>
        This Site is provided "as is" and "as available." We do not warrant that
        the Site will be error-free or uninterrupted, or that any information on
        the Site is complete, accurate, or current at any given moment. Pricing
        shown on the Site is an estimate only and does not constitute a binding
        offer until confirmed in writing.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, {COMPANY.name} and its owners,
        employees, and agents are not liable for any indirect, incidental,
        consequential, special, or punitive damages arising from your use of
        this Site. Our liability for services performed is governed by the
        Customer Contract.
      </p>

      <h2>12. Third-Party Links</h2>
      <p>
        This Site may link to third-party websites (Google Maps, Facebook,
        YouTube, BBB). We are not responsible for the content, privacy
        practices, or policies of any third-party site.
      </p>

      <h2>13. Privacy</h2>
      <p>
        Information you provide through the quote form (name, phone, email,
        address, service needed) is used only to follow up with your request,
        schedule work, and communicate about your job. We do not sell your
        personal information. Aggregate, privacy-friendly analytics
        (Plausible.io) are used on this Site; no personal data is tracked or
        sold. For full details, see our{" "}
        <a href="/privacy" className="text-[#1E88E5] hover:text-[#42A5F5]">
          Privacy Policy
        </a>
        .
      </p>

      <h2>14. Changes to These Terms</h2>
      <p>
        We may update these Terms at any time. The "Last updated" date at the
        top of this page reflects the most recent revision. Continued use of
        the Site after changes constitutes acceptance.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms? Email{" "}
        <a href={`mailto:${COMPANY.email}`} className="text-[#1E88E5] hover:text-[#42A5F5]">
          {COMPANY.email}
        </a>{" "}
        or call/text {COMPANY.phone}.
      </p>
    </LegalLayout>
  );
}
